import {inject, injectable} from 'inversify'
import {DeliveryServiceInterface} from '@/src/ports/services'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import {IErrorReporterSymbol} from '@/src/ports/services/monitoring'
import type {IApiClientServiceFactory} from '@/src/ports/services/api'
import {IApiClientServiceFactorySymbol} from '@/src/ports/services/api'
import {DeliveryDetailsResponse, DeliveryListResponse, DeliveryType,} from '@/src/core/domain/entities/delivery'
import {DeliveryError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import {PersonTypeVariant} from '@/src/entities/order'
import {Routes} from '@/src/config/routes'

/**
 * Delivery Service Implementation
 *
 * Handles delivery-related API calls.
 * Returns Result type for explicit error handling.
 */
@injectable()
export class DeliveryService implements DeliveryServiceInterface {
  constructor(
    @inject(IErrorReporterSymbol) private errorReporter: IErrorReporter,
    @inject(IApiClientServiceFactorySymbol) private apiClientFactory: IApiClientServiceFactory,
  ) {}

  async getTypes(
    locationId: string,
    personType: PersonTypeVariant,
    customLocationName?: string,
  ): Promise<OperationResult<DeliveryType[], DeliveryError>> {
    try {
      const client = this.apiClientFactory()
      const deliveryTypes = await client.post<DeliveryListResponse>(
        Routes.webApi.order.deliveryTypes,
        {
          data: {
            person_type: personType,
            location_id: locationId,
            custom_location: customLocationName || undefined
          },
        },
      )

      return Result.ok(deliveryTypes)
    } catch (e: unknown) {
      this.errorReporter.capture(e instanceof Error ? e : new Error(String(e)), {
        location_id: locationId,
        person_type: personType,
      })
      return Result.fail(new DeliveryError('GET_TYPES_FAILED', e))
    }
  }

  async getDetails(
    locationId: string,
    personType: PersonTypeVariant,
    customLocationName: string | undefined,
    deliveryServiceId: Id,
  ): Promise<OperationResult<DeliveryDetailsResponse, DeliveryError>> {
    try {
      const client = this.apiClientFactory()
      const details = await client.post<DeliveryDetailsResponse>(
        Routes.webApi.order.deliveryTypeDetails,
        {
          data: {
            person_type: personType,
            location_id: locationId,
            delivery_service_id: deliveryServiceId,
            custom_location: customLocationName || undefined
          },
        },
      )

      return Result.ok(details)
    } catch (e: unknown) {
      this.errorReporter.capture(e instanceof Error ? e : new Error(String(e)), {
        location_id: locationId,
        delivery_service_id: deliveryServiceId,
        person_type: personType,
      })
      return Result.fail(new DeliveryError('GET_DETAILS_FAILED', e))
    }
  }
}
